import React, {Fragment, useState} from 'react';
import {Outlet, Navigate} from 'react-router-dom';
import Sidebar from './Sidebar';
import {useStateContext} from '../../contexts/ContextProvider';
import {ToastContainer} from 'react-toastify';
import {useInitAuthenticatedUser} from "../../hooks/useInitAuthenticatedUser";
import TeacherSidebar from "./TeacherSidebar";
import {USER_TYPE_SUPER_ADMIN, USER_TYPE_TEACHER} from "../../utility/utility";
import {Dialog, Menu, Transition} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBars, faXmark, faChevronDown, faRightFromBracket} from "@fortawesome/free-solid-svg-icons";

const UserMenu = () => {
    const {user, setUser, setToken} = useStateContext();

    const handleLogoutClick = () => {
        setToken(null);
        setUser({});
    };

    const initial = user?.name ? user.name.charAt(0).toUpperCase() : '?';

    return (
        <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-3 hover:bg-gray-100 transition-colors">
                <div className="h-8 w-8 rounded-full bg-pink-600 text-white flex items-center justify-center text-sm font-semibold">
                    {initial}
                </div>
                <span className="hidden sm:block text-sm font-medium text-gray-700">{user?.name || 'Account'}</span>
                <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-400"/>
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-gray-950/5 focus:outline-none z-20">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <div className="p-1">
                        <Menu.Item>
                            {({active}) => (
                                <button
                                    onClick={handleLogoutClick}
                                    className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-left transition-colors ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}
                                >
                                    <FontAwesomeIcon icon={faRightFromBracket} className="w-4 text-gray-400"/>
                                    Log out
                                </button>
                            )}
                        </Menu.Item>
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

const DashboardLayout = () => {
    useInitAuthenticatedUser();
    const {user, token} = useStateContext();
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    if (!token) {
        return <Navigate to="/login"/>;
    }

    const activeSidebar = user.type === USER_TYPE_SUPER_ADMIN
        ? <Sidebar/>
        : (user.type === USER_TYPE_TEACHER ? <TeacherSidebar/> : null);

    return (
        <Fragment>
            <div className="flex h-screen bg-gray-50">
                {/* Mobile drawer: slides the sidebar over the content with a
                    backdrop, instead of squeezing the content next to it */}
                <Transition.Root show={isSidebarOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                        <Transition.Child
                            as={Fragment}
                            enter="transition-opacity ease-linear duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity ease-linear duration-300"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"/>
                        </Transition.Child>

                        <div className="fixed inset-0 flex">
                            <Transition.Child
                                as={Fragment}
                                enter="transition ease-in-out duration-300 transform"
                                enterFrom="-translate-x-full"
                                enterTo="translate-x-0"
                                leave="transition ease-in-out duration-300 transform"
                                leaveFrom="translate-x-0"
                                leaveTo="-translate-x-full"
                            >
                                <Dialog.Panel className="relative flex w-64 max-w-[80vw]">
                                    <Transition.Child
                                        as={Fragment}
                                        enter="ease-in-out duration-300"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="ease-in-out duration-300"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <div className="absolute top-4 right-0 -mr-11 flex w-11 justify-center">
                                            <button
                                                type="button"
                                                className="h-9 w-9 flex items-center justify-center rounded-lg text-white hover:bg-white/10"
                                                onClick={() => setSidebarOpen(false)}
                                            >
                                                <FontAwesomeIcon icon={faXmark} className="h-5 w-5"/>
                                            </button>
                                        </div>
                                    </Transition.Child>
                                    {activeSidebar}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </Dialog>
                </Transition.Root>

                {/* Desktop sidebar: always visible, part of the layout */}
                <div className="hidden lg:flex lg:flex-shrink-0">
                    {activeSidebar}
                </div>

                <div className="flex flex-col flex-1 overflow-hidden">
                    <header className="bg-white border-b border-gray-200">
                        <div className="mx-auto py-3 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                            <div className="flex items-center">
                                {/* Hamburger menu */}
                                <button
                                    className="lg:hidden mr-3 h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                                    onClick={() => setSidebarOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faBars} className="h-5 w-5"/>
                                </button>

                                {/* Dashboard title */}
                                <h1 className="text-lg font-semibold leading-tight text-gray-900">
                                    Dashboard
                                </h1>
                            </div>

                            <UserMenu/>
                        </div>
                    </header>
                    <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                        <Outlet/>
                    </main>
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={1500}/>
        </Fragment>
    );
};

export default DashboardLayout;
